import React, { useMemo } from 'react';
import { Trophy, TrendingUp, TrendingDown, Flame, X } from 'lucide-react';
import { completionRate, calculateStreak, getLocalDateStr, addDays, isHabitDueOnDate } from '../../lib/habitUtils';

export default function WeeklyReport({ habits, allLogs, onClose }) {
  const activeHabits = habits.filter(h => h.status !== 'archived' && h.status !== 'paused');

  const report = useMemo(() => {
    const today = getLocalDateStr();
    // This week Mo-So
    const dow = new Date(today + 'T12:00:00').getDay();
    const mondayOffset = dow === 0 ? -6 : 1 - dow;
    const monday = addDays(today, mondayOffset);
    const sunday = addDays(monday, 6);

    let totalDue = 0, totalDone = 0;
    const habitStats = activeHabits.map(h => {
      const logs = allLogs[h.id] || [];
      let due = 0, done = 0;
      for (let i = 0; i < 7; i++) {
        const d = addDays(monday, i);
        if (isHabitDueOnDate(h, d) && d >= h.startDate && d <= today) {
          due++;
          if (logs.some(l => l.date === d && l.done)) done++;
        }
      }
      totalDue += due;
      totalDone += done;
      return {
        id: h.id,
        title: h.title,
        weekRate: due > 0 ? Math.round((done / due) * 100) : 0,
        weekDone: done,
        weekDue: due,
        streak: calculateStreak(h, logs),
        rate7: completionRate(h, logs, 7),
        prevRate7: completionRate(h, logs.filter(l => l.date < monday), 7)
      };
    });

    const weekRate = totalDue > 0 ? Math.round((totalDone / totalDue) * 100) : 0;
    const best = [...habitStats].sort((a, b) => b.weekRate - a.weekRate)[0];
    const improving = habitStats.filter(h => h.rate7 > h.prevRate7 + 10);
    const declining = habitStats.filter(h => h.rate7 < h.prevRate7 - 10);
    const longestStreak = [...habitStats].sort((a, b) => b.streak - a.streak)[0];

    return { weekRate, totalDone, totalDue, best, improving, declining, longestStreak, habitStats };
  }, [activeHabits, allLogs]);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-t-2xl sm:rounded-2xl w-full sm:max-w-md max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="sticky top-0 bg-white border-b border-slate-100 px-5 py-3 flex items-center justify-between rounded-t-2xl">
          <div className="flex items-center gap-2">
            <Trophy className="w-5 h-5 text-amber-500" />
            <h2 className="font-bold text-slate-800">Wochenreport</h2>
          </div>
          <button onClick={onClose} className="p-1 text-slate-400 hover:text-slate-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {/* Overall rate */}
          <div className="text-center">
            <p className="text-4xl font-bold text-slate-800">{report.weekRate}%</p>
            <p className="text-sm text-slate-500">{report.totalDone}/{report.totalDue} erledigt diese Woche</p>
            <div className="w-full bg-slate-100 rounded-full h-2 mt-2">
              <div
                className={`h-2 rounded-full ${report.weekRate >= 70 ? 'bg-emerald-500' : report.weekRate >= 40 ? 'bg-amber-500' : 'bg-red-400'}`}
                style={{ width: `${report.weekRate}%` }}
              />
            </div>
          </div>

          {/* Highlights */}
          <div className="grid grid-cols-2 gap-3">
            {report.best && (
              <div className="bg-emerald-50 rounded-xl p-3">
                <p className="text-[10px] text-emerald-600 font-medium mb-1">Bestes Habit</p>
                <p className="text-sm font-bold text-emerald-800 truncate">{report.best.title}</p>
                <p className="text-xs text-emerald-600">{report.best.weekRate}%</p>
              </div>
            )}
            {report.longestStreak && report.longestStreak.streak > 0 && (
              <div className="bg-orange-50 rounded-xl p-3">
                <div className="flex items-center gap-1 mb-1">
                  <Flame className="w-3 h-3 text-orange-500" />
                  <p className="text-[10px] text-orange-600 font-medium">Längster Streak</p>
                </div>
                <p className="text-sm font-bold text-orange-800 truncate">{report.longestStreak.title}</p>
                <p className="text-xs text-orange-600">{report.longestStreak.streak} Tage</p>
              </div>
            )}
          </div>

          {/* Trends */}
          {report.improving.length > 0 && (
            <div className="bg-blue-50 rounded-xl p-3">
              <div className="flex items-center gap-1 mb-1">
                <TrendingUp className="w-3.5 h-3.5 text-blue-600" />
                <p className="text-xs font-medium text-blue-700">Verbesserungen</p>
              </div>
              {report.improving.map(h => (
                <p key={h.id} className="text-sm text-blue-800">{h.title}: {h.prevRate7}% → {h.rate7}%</p>
              ))}
            </div>
          )}

          {report.declining.length > 0 && (
            <div className="bg-red-50 rounded-xl p-3">
              <div className="flex items-center gap-1 mb-1">
                <TrendingDown className="w-3.5 h-3.5 text-red-500" />
                <p className="text-xs font-medium text-red-700">Braucht Aufmerksamkeit</p>
              </div>
              {report.declining.map(h => (
                <p key={h.id} className="text-sm text-red-800">{h.title}: Tipp - nur die 1%-Version machen!</p>
              ))}
            </div>
          )}

          {/* Per habit */}
          <div>
            <h3 className="text-sm font-semibold text-slate-700 mb-2">Alle Habits</h3>
            <div className="space-y-1.5">
              {report.habitStats.sort((a, b) => b.weekRate - a.weekRate).map(h => (
                <div key={h.id} className="flex items-center gap-2 p-2 bg-slate-50 rounded-lg">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-slate-700 truncate">{h.title}</p>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs text-slate-500">{h.weekDone}/{h.weekDue}</span>
                    <div className="w-12 bg-slate-200 rounded-full h-1.5">
                      <div className={`h-1.5 rounded-full ${h.weekRate >= 70 ? 'bg-emerald-500' : h.weekRate >= 40 ? 'bg-amber-500' : 'bg-red-400'}`}
                        style={{ width: `${h.weekRate}%` }} />
                    </div>
                    <span className="text-xs font-medium text-slate-600 w-7 text-right">{h.weekRate}%</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Motivation */}
          <div className="text-center py-2">
            <p className="text-sm text-slate-600">
              {report.weekRate >= 80 ? 'Starke Woche! Weiter so.' :
               report.weekRate >= 50 ? 'Solide Basis. Nächste Woche noch einen drauf!' :
               'Neustart zählt. Nächste Woche nur die 1%-Versionen.'}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
